<template>
  <div class="stability-card">
    <div
        v-if="mainItem"
        class="stability-card__group"
    >
      <div class="stability-card__main">
        <div class="stability-card__main-left">
          <span
              class="stability-card__mark stability-card__mark--main"
              :style="{ backgroundColor: mainItem.color }"
          ></span>

          <div class="stability-card__label-group">
            <span class="stability-card__main-label">
              {{ mainItem.label }}
            </span>

            <a-popover
                v-if="mainItem.warning"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="stability-card__popover">
                  {{ mainItem.warning }}
                </div>
              </template>

              <InfoCircleOutlined class="stability-card__warning-icon" />
            </a-popover>
          </div>
        </div>

        <div class="stability-card__main-right">
          <span
              class="stability-card__change stability-card__change--main"
              :class="{
                'positive-change': mainItem.change > 0,
                'negative-change': mainItem.change < 0,
                'neutral-change': mainItem.change === 0
              }"
          >
            {{ getChangeText(mainItem.change) }}
          </span>

          <span class="stability-card__value stability-card__value--main">
            {{ mainItem.value }}
          </span>
        </div>
      </div>

      <div
          v-if="secondaryItems.length"
          class="stability-card__children"
      >
        <div
            v-for="item in secondaryItems"
            :key="item.key"
            class="stability-card__legend-row stability-card__legend-row--child"
        >
          <div class="stability-card__legend-name">
            <span
                class="stability-card__mark"
                :style="{ backgroundColor: item.color }"
            ></span>

            <div class="stability-card__label-group">
              <span class="stability-card__legend-label">
                {{ item.label }}
              </span>

              <a-popover
                  v-if="item.warning"
                  trigger="hover"
                  placement="top"
              >
                <template #content>
                  <div class="stability-card__popover">
                    {{ item.warning }}
                  </div>
                </template>

                <InfoCircleOutlined class="stability-card__warning-icon" />
              </a-popover>
            </div>
          </div>

          <span
              class="stability-card__change"
              :class="{
                'positive-change': item.change > 0,
                'negative-change': item.change < 0,
                'neutral-change': item.change === 0
              }"
          >
            {{ getChangeText(item.change) }}
          </span>

          <span class="stability-card__value">
            {{ item.value }}
          </span>
        </div>
      </div>
    </div>

    <div
        ref="chartWrapper"
        class="stability-card__chart"
    >
      <canvas ref="chartCanvas"></canvas>

      <div
          v-if="!hasChartData"
          class="stability-card__chart-empty"
      >
        Не хватает данных за текущий период
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from 'chart.js'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Filler,
)

export default {
  name: 'StabilityMotivationIndexCard',

  components: {
    InfoCircleOutlined,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartDatasets: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartWrapper = ref(null)

    let chartInstance = null
    let resizeObserver = null
    let resizeFrame = null

    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const normalizedItems = computed(() => {
      return props.items.map((item, index) => ({
        ...item,
        key: item.key ?? item.label ?? `item-${index}`,
        change: toNumber(item.change),
        value: toNumber(item.value),
      }))
    })

    const mainItem = computed(() => {
      return (
          normalizedItems.value.find((item) => item.key === 'resilience') ||
          normalizedItems.value[0] ||
          null
      )
    })

    const secondaryItems = computed(() => {
      if (!mainItem.value) {
        return []
      }

      return normalizedItems.value.filter((item) => item.key !== mainItem.value.key)
    })

    const normalizedChartLabels = computed(() => {
      return Array.isArray(props.chartLabels) ? props.chartLabels : []
    })

    const normalizedChartDatasets = computed(() => {
      return props.chartDatasets.map((item, index) => ({
        ...item,
        key: item.key ?? item.label ?? `dataset-${index}`,
        values: Array.isArray(item.values)
            ? item.values.map((value) => {
              const numericValue = Number(value)
              return Number.isFinite(numericValue) ? numericValue : null
            })
            : [],
      }))
    })

    const hasChartData = computed(() => {
      if (!normalizedChartDatasets.value.length) {
        return false
      }

      return normalizedChartDatasets.value.some((dataset) => {
        const validValues = dataset.values.filter((value) => value !== null)
        return validValues.length >= 2
      })
    })

    const mainDatasetKey = computed(() => {
      const resilienceDataset = normalizedChartDatasets.value.find(
          (item) => item.key === 'resilience'
      )

      if (resilienceDataset) {
        return resilienceDataset.key
      }

      return normalizedChartDatasets.value[0]?.key ?? null
    })

    const chartDatasetList = computed(() => {
      if (!hasChartData.value) {
        return normalizedChartDatasets.value.map((item) => ({
          label: item.label,
          data: normalizedChartLabels.value.map(() => null),
          borderColor: 'rgba(0,0,0,0)',
          backgroundColor: 'rgba(0,0,0,0)',
          borderWidth: 0,
          borderDash: [],
          pointRadius: 0,
          pointHoverRadius: 0,
          pointBackgroundColor: 'rgba(0,0,0,0)',
          pointBorderColor: 'rgba(0,0,0,0)',
          fill: false,
          tension: 0,
          spanGaps: false,
        }))
      }

      return normalizedChartDatasets.value.map((item) => {
        const isMain = item.key === mainDatasetKey.value

        return {
          label: item.label,
          data: item.values,
          borderColor: item.color,
          backgroundColor: isMain ? 'rgba(76, 110, 245, 0.12)' : item.color,
          borderWidth: isMain ? 2 : 1,
          borderDash: isMain ? [] : [6, 6],
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: item.color,
          pointBorderColor: item.color,
          fill: isMain ? 'start' : false,
          tension: 0,
          spanGaps: false,
          order: isMain ? 10 : 1,
        }
      })
    })

    const getChangeText = (value) => {
      if (value > 0) {
        return `↑ ${value}`
      }

      if (value < 0) {
        return `↓ ${Math.abs(value)}`
      }

      return '–'
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const forceResizeChart = () => {
      if (!chartInstance || !chartWrapper.value) {
        return
      }

      const width = chartWrapper.value.clientWidth
      const height = chartWrapper.value.clientHeight

      if (!width || !height) {
        return
      }

      chartInstance.resize(width, height)
      chartInstance.update('none')
    }

    const scheduleResize = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
      }

      resizeFrame = requestAnimationFrame(() => {
        forceResizeChart()
      })
    }

    const createChart = () => {
      if (!chartCanvas.value) {
        return
      }

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: normalizedChartLabels.value,
          datasets: chartDatasetList.value,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          resizeDelay: 0,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: hasChartData.value,
              displayColors: true,
            },
            datalabels: {
              display: false,
            },
          },
          layout: {
            padding: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },
          scales: {
            x: {
              offset: false,
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
                display: hasChartData.value,
              },
              border: {
                display: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
            },
            y: {
              min: -10,
              max: 10,
              ticks: {
                stepSize: 5,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              grid: {
                drawTicks: false,
                color: (context) => {
                  const value = context.tick?.value
                  return value === 0 ? '#EEF0F3' : 'rgba(0,0,0,0)'
                },
                lineWidth: (context) => {
                  const value = context.tick?.value
                  return value === 0 ? 1 : 0
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
      })
    }

    const renderChart = async () => {
      await nextTick()

      destroyChart()
      createChart()

      await nextTick()
      scheduleResize()
    }

    const handleWindowResize = () => {
      scheduleResize()
    }

    onMounted(async () => {
      await renderChart()

      window.addEventListener('resize', handleWindowResize)

      if (chartWrapper.value) {
        resizeObserver = new ResizeObserver(() => {
          scheduleResize()
        })

        resizeObserver.observe(chartWrapper.value)
      }

      setTimeout(() => {
        scheduleResize()
      }, 50)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleWindowResize)

      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }

      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
        resizeFrame = null
      }

      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartDatasets],
        async () => {
          await renderChart()
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      chartWrapper,
      mainItem,
      secondaryItems,
      hasChartData,
      getChangeText,
    }
  },
}
</script>

<style scoped lang="scss">
.stability-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.stability-card__group {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
}

.stability-card__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 16px;
  min-width: 0;
}

.stability-card__main-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.stability-card__label-group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.stability-card__main-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 120%;
}

.stability-card__main-right {
  display: grid;
  grid-template-columns: minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  column-gap: 16px;
  flex: 0 0 auto;
}

.stability-card__children {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
  margin-left: 20px;
}

.stability-card__legend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  column-gap: 12px;
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.stability-card__legend-row--child {
  position: relative;
}

.stability-card__legend-name {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.stability-card__legend-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.stability-card__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.stability-card__mark--main {
  width: 13px;
  height: 13px;
}

.stability-card__warning-icon {
  color: #FAAD14;
  font-size: 13px;
  flex: 0 0 auto;
  cursor: pointer;
}

.stability-card__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.stability-card__change {
  min-width: 0;
  text-align: right;
  white-space: nowrap;
  font-size: 14px;
}

.stability-card__change--main {
  font-style: normal;
  font-size: 14px;
  line-height: 120%;
}

.stability-card__value {
  min-width: 50px;
  text-align: right;
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
  font-size: 14px;
}

.stability-card__value--main {
  min-width: 50px;
  font-style: normal;
  font-size: 14px;
  line-height: 120%;
  font-weight: 600;
}

.stability-card__chart {
  position: relative;
  width: 100%;
  height: 180px;
  margin-top: auto;
  min-width: 0;
}

.stability-card__chart canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.stability-card__chart-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  white-space: nowrap;
}

.positive-change {
  color: #F55565;
}

.negative-change {
  color: var(--main-green);
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>